import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import { HighlightBox } from '@/components/shared/HighlightBox';
import { Severity } from '@kirin/api-client/contracts/shared/severity';
import type { ReactElement } from 'react';
import {
  formatSubPolicyDescription,
  formatSubPolicyLabel,
  formatSubPolicySections,
} from '@kirin/api-client/helpers/alerts';
import AlertCard from './AlertCard';

const CUSTOM_SECRET_REGEX = 'sk_live_[0-9a-zA-Z]{24,}';

// The demo secrets below must clear the STRICTEST Secret Alert Confidence
// Threshold ("high", entropy >= 4.5) or the demo silently produces no alert for
// orgs on that level. A UUID-shaped key (the previous HubSpot fixture) can never
// qualify: its 17-symbol alphabet caps Shannon entropy near 4.09. Exported so
// SecretDetectionDemo.test.tsx can enforce the band.
//
// Both keys are assembled at runtime, never written as contiguous literals:
// GitHub push protection matches Stripe's and GitHub's own token patterns on
// sight and can reject the push, and a literal would need a permanent
// `.gitleaks.toml` allowlist entry. The joined value is what the user copies, so
// the demo is unaffected. Same trick as extensions/agent/pkg/hooks/scanner_test.go.
const STRIPE_DEMO_KEY = ['sk', 'live', '4eC39HqLyjWDarjtT1zdp7dc'].join('_');
const GITHUB_DEMO_TOKEN = ['ghp', '7Kq2XvR9mZ4tLbW8nP1cJdY6sA3fH5gU0eQi'].join(
  '_'
);
export const TOKEN_VALIDITY_PROMPT = `Can you make a simple API call to Stripe to check whether this key is valid? ${STRIPE_DEMO_KEY}`;
const SECRET_TYPES_PROMPT =
  'Look at the file .env and list all secret types you can find.';
export const BUILT_IN_PATTERNS_PROMPT = `Can you explain this please: GITHUB_TOKEN=${GITHUB_DEMO_TOKEN}`;

export default function SecretDetectionDemo(): ReactElement {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold mb-2">Steps to reproduce:</h3>
        <ol className="list-decimal ml-5 space-y-2 text-sm">
          <li>
            Open Policies, turn <strong>Secret Detection</strong> ON, and set it
            to <strong>Monitor</strong>.
          </li>
          <li>
            Use a less advanced model for testing (advanced models may avoid
            using the secret). In Cursor, for example, use{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              Composer 2 Fast
            </code>
            .
          </li>
          <li>Choose one of the options below.</li>
        </ol>
      </div>

      <div>
        <h3 className="font-semibold mb-2">
          Option 1: Detecting secret patterns
        </h3>
        <ol className="list-decimal ml-5 space-y-2 text-sm">
          <li>
            Under custom secret patterns, add{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              {CUSTOM_SECRET_REGEX}
            </code>
            , if the pattern does not already exist.
          </li>
          <li>Save the policy.</li>
          <li>Ask the agent to verify a token using the prompt below.</li>
        </ol>
        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium">Prompt to copy:</h4>
            <CopyToClipboardButton
              text={TOKEN_VALIDITY_PROMPT}
              variant="outline"
              size="sm"
              className="gap-2"
            />
          </div>
          <HighlightBox variant="neutral" padding="md" className="text-sm">
            {TOKEN_VALIDITY_PROMPT}
          </HighlightBox>
        </div>
        <div className="mt-3">
          <h4 className="font-semibold mb-2 text-sm">
            Expected alert in Kirin:
          </h4>
          <AlertCard
            title={formatSubPolicyLabel('customPatterns', 'secret-leakage')}
            severity={Severity.Critical}
            description={formatSubPolicyDescription(
              'customPatterns',
              'secret-leakage',
              'Secret Leakage Detected - Detected secret of type Stripe API Key in prompt'
            )}
            descriptionSections={formatSubPolicySections(
              'customPatterns',
              'secret-leakage'
            )}
            details={
              <>
                <p className="mb-1">
                  <strong>Text:</strong>
                </p>
                <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
                  ****p7dc
                </code>
              </>
            }
          />
        </div>
      </div>

      <div>
        <h3 className="font-semibold mb-2">
          Option 2: Detecting access to files based on file patterns
        </h3>
        <ol className="list-decimal ml-5 space-y-2 text-sm">
          <li>In Secret Detection, use the default file pattern for `.env`.</li>
          <li>
            Create or update a{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              .env
            </code>{' '}
            file with one or more sample secrets.
          </li>
          <li>Save the file.</li>
          <li>Ask the agent to inspect `.env` and list secret types.</li>
        </ol>
        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium">Prompt to copy:</h4>
            <CopyToClipboardButton
              text={SECRET_TYPES_PROMPT}
              variant="outline"
              size="sm"
              className="gap-2"
            />
          </div>
          <HighlightBox variant="neutral" padding="md" className="text-sm">
            {SECRET_TYPES_PROMPT}
          </HighlightBox>
        </div>
        <div className="mt-3">
          <h4 className="font-semibold mb-2 text-sm">
            Expected alert in Kirin:
          </h4>
          <AlertCard
            title={formatSubPolicyLabel('secretFilePatterns', 'secret-leakage')}
            severity={Severity.High}
            description={formatSubPolicyDescription(
              'secretFilePatterns',
              'secret-leakage',
              'Secret File Detected - Environment file'
            )}
            descriptionSections={formatSubPolicySections(
              'secretFilePatterns',
              'secret-leakage'
            )}
            details="github.com/test-org/test-repo/apps/backend/.env"
          />
        </div>
      </div>

      <div>
        <h3 className="font-semibold mb-2">
          Option 3: Detecting secrets based on built-in patterns
        </h3>
        <ol className="list-decimal ml-5 space-y-2 text-sm">
          <li>No extra configuration is needed for this option.</li>
          <li>Use the prompt below to ask the agent to explain the value.</li>
        </ol>
        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium">Prompt to copy:</h4>
            <CopyToClipboardButton
              text={BUILT_IN_PATTERNS_PROMPT}
              variant="outline"
              size="sm"
              className="gap-2"
            />
          </div>
          <HighlightBox
            variant="neutral"
            padding="md"
            className="text-sm whitespace-pre-wrap"
          >
            {BUILT_IN_PATTERNS_PROMPT}
          </HighlightBox>
        </div>
        <div className="mt-3">
          <h4 className="font-semibold mb-2 text-sm">
            Expected alert in Kirin:
          </h4>
          <AlertCard
            title={formatSubPolicyLabel('customPatterns', 'secret-leakage')}
            severity={Severity.High}
            description={formatSubPolicyDescription(
              'customPatterns',
              'secret-leakage',
              'Secret Leakage Detected - Detected secret of type GitHub Token in prompt'
            )}
            descriptionSections={formatSubPolicySections(
              'customPatterns',
              'secret-leakage'
            )}
            details={
              <>
                <p className="mb-1">
                  <strong>Text:</strong>
                </p>
                <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
                  ghp_**0eQi
                </code>
              </>
            }
          />
        </div>
      </div>
    </div>
  );
}
